// "Use it up" Today-dashboard tile — shows the top 2-3 pantry-based
// recipe suggestions, surfacing items that are about to expire.
//
// Renders null when the suggestions list is empty so it never occupies
// space in the dashboard.  When suggestions are present it mirrors the
// sibling Tile structure in today/index.tsx (today-tile wrapper, header,
// list rows).
//
// Tapping a row navigates directly to that recipe: sets the food tab's
// initial mode to "recipes" AND sets the initial recipe id so the detail
// panel opens immediately.

import { useEffect, useState } from "preact/hooks";
import { api, type RecipeSuggestion } from "../../lib/rpc";
import { Icon } from "../../icons";

interface UseItUpCardProps {
  setTab: (id: string) => void;
  setFoodInitialMode: (mode: "recipes") => void;
  setFoodInitialRecipeId: (id: string) => void;
}

function expiryPhrase(days: number | null): string {
  if (days === null) return "expiring soon";
  if (days < 0) return "expired";
  if (days === 0) return "expires today";
  if (days === 1) return "expires tomorrow";
  return `expires in ${days} days`;
}

export function UseItUpCard({ setTab, setFoodInitialMode, setFoodInitialRecipeId }: UseItUpCardProps) {
  const [suggestions, setSuggestions] = useState<RecipeSuggestion[]>([]);

  useEffect(() => {
    let cancelled = false;
    api.recipesSuggestions(3)
      .then((list) => { if (!cancelled) setSuggestions(list); })
      .catch(() => { /* best-effort */ });
    return () => { cancelled = true; };
  }, []);

  if (suggestions.length === 0) return null;

  const handleRowClick = (recipeId: number) => {
    setFoodInitialMode("recipes");
    setFoodInitialRecipeId(String(recipeId));
    setTab("food");
  };

  return (
    <div className="today-tile" onClick={() => setTab("food")}>
      <div className="today-tile-hd">
        <div>
          <h3>Use it up</h3>
          <div className="today-tile-sub">
            {suggestions.length === 1 ? "1 suggestion" : `${suggestions.length} suggestions`}
          </div>
        </div>
        <span className="today-tile-go" aria-hidden>
          <Icon name="arrow-right" size={16} />
        </span>
      </div>
      <div className="today-list">
        {suggestions.map((s) => {
          const { recipe, topExpiringItems, matched, missing } = s;
          const total = matched.length + missing.length;
          const top = topExpiringItems[0];

          return (
            <div
              key={recipe.id}
              className="today-use-it-up-row"
              onClick={(e) => {
                e.stopPropagation();
                handleRowClick(recipe.id);
              }}
            >
              {recipe.imageUrl ? (
                <div className="today-use-it-up-thumb">
                  <img src={recipe.imageUrl} alt={recipe.name} loading="lazy" />
                </div>
              ) : null}
              <div className="today-use-it-up-meta">
                <div className="today-use-it-up-title">{recipe.name}</div>
                <div className="today-use-it-up-sub">
                  {top && (
                    <span>
                      Uses your {top.name} — {expiryPhrase(top.days)}
                    </span>
                  )}
                  {top && " · "}
                  <span>Have {matched.length}/{total}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
